import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { RoleType, UserStatus } from "../dto/interfaces";
import { QueryClient, QueryClientProvider } from "react-query";
import Cookies from "js-cookie";
import axios from "axios";
import { navigate } from "gatsby";
import { URL_API } from "../utils/constants";
import { set } from "react-hook-form";
import { type } from "os";

export enum Language {
  ENGLISH = "ENGLISH",
  SPANISH = "SPANISH",
  GERMAN = "GERMAN",
  PORTUGUESE = "PORTUGUESE",
  FRENCH = "FRENCH",
  ITALIAN = "ITALIAN",
}

export enum PaymentMethod {
  CHECK = "CHECK",
  CREDIT_CARD = "CREDIT_CARD",
  PAYPAL = "PAYPAL",
  CRYPTO = "CRYPTO",
  CASH = "CASH",
  OTHER = "OTHER",
}

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
  OTHER = "OTHER",
}

interface Specialization {
  id: number;
  key: string;
  en: string;
  es: string;
  createdAt: Date;
  updatedAt: Date;
}

interface PracticeArea {
  id: number;
  key: string;
  en: string;
  es: string;
  createdAt: Date;
  updatedAt: Date;
}

interface UserProfessional {
  id: number;
  specializations: number[];
  practiceAreas: number[];
  languages: Language[];
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserPaymentMethod {
  id: number;
  methods: PaymentMethod[];
  hourlyRate: number;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserEducation {
  id: number;
  university: string;
  degree: string;
  year: number;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserCertification {
  id: number;
  title: string;
  organization: string;
  year: number;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}
interface UserArticle {
  id: number;
  title: string;
  organization: string;
  year: number;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserAssociation {
  id: number;
  title: string;
  organization: string;
  year: number;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserAddress {
  id: number;
  province: string;
  canton: string;
  district: string;
  exactAddress: string;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserAbout {
  id: number;
  avatar: string;
  gender: Gender;
  dateOfBirth: Date;
  aboutMe: string;
  userProfile: UserProfile;
  createdAt: Date;
  updatedAt: Date;
}

interface UserProfile {
  id: number;
  firmName: string;
  dni: string;
  facebook: string;
  instagram: string;
  licenseNumber: string;
  website: string;
  certifications: UserCertification[];
  educations: UserEducation[];
  articles: UserArticle[];
  associations: UserAssociation[];
  address: UserAddress;
  about: UserAbout;
  paymentMethod: UserPaymentMethod;
  professional: UserProfessional;
  createdAt: Date;
  updatedAt: Date;
  typeOfConsultation: string[];
}

interface UserLogin {
  id: string;
  email: string;
  fullName: string;
  role: RoleType;
  status: UserStatus;
  phoneNumber?: string;
  userProfile: UserProfile;
  favoriteAttorneys?: string[];
}

interface AppContextProps {
  accessToken?: string;
  user?: UserLogin;
  specializations?: Specialization[];
  practiceAreas?: PracticeArea[];
  firmNames?: string[];
  role: RoleType;
  signOut: (fromHome?: boolean) => void;
  setRole: (newRole: RoleType) => void;
  setContextValue: (key: keyof AppContextProps, value: any) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const useAppContext = (): AppContextProps => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState<RoleType>(RoleType.BASIC);
  const [user, setUser] = useState<UserLogin | undefined>(undefined);
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);
  const [firmNames, setFirmNames] = useState<string[]>([]);
  const [accessToken, setAccessToken] = useState<string | undefined>(
    Cookies.get("accessToken")
  );
  const signOut = (fromHome = false) => {
    setRole(RoleType.BASIC);
    setUser(undefined);
    setAccessToken(undefined);
    Cookies.remove("accessToken");
    if (!fromHome) {
      navigate("/auth/login");
    }
  };

  useEffect(() => {
    if (accessToken && !user) {
      console.log("dispatch check");
      axios
        .get(`${URL_API}/auth/check`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then(({ data }) => {
          console.log("get data", data);
          setContextValue("user", { ...data });
          setContextValue("role", data.role);
          if (data.role === RoleType.ATTORNEY) {
            axios
              .get(`${URL_API}/users/profile/${data.id}`, {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              })
              .then(({ data }: any) => {
                const { user, ...userProfile } = data;
                setContextValue("user", {
                  ...user,
                  userProfile,
                });
              });
          }
        })
        .catch((e) => {
          console.log("dispara catch", e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [accessToken, user]);

  useEffect(() => {
    if (accessToken) {
      Cookies.set("accessToken", accessToken);
    }
  }, [accessToken]);

  const handleSetRole = (newRole: RoleType) => {
    setRole(newRole);
  };

  const stateSetters = {
    role: setRole,
    user: setUser,
    accessToken: setAccessToken,
    specializations: setSpecializations,
    practiceAreas: setPracticeAreas,
    firmNames: setFirmNames,
  };

  const setContextValue = (key: keyof AppContextProps, value: any) => {
    const setter =
      stateSetters[
        key as
          | "role"
          | "user"
          | "accessToken"
          | "specializations"
          | "practiceAreas"
          | "firmNames"
      ];

    if (setter) {
      setter(value);
    }
  };

  const appContextValue: AppContextProps = {
    role,
    setRole: handleSetRole,
    user,
    accessToken,
    setContextValue,
    signOut,
    specializations,
    practiceAreas,
    firmNames,
  };

  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider value={appContextValue}>
        {!isLoading && children}
      </AppContext.Provider>
    </QueryClientProvider>
  );
};
